import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, Loader2, Plus, X, ArrowRightLeft, BookmarkPlus, Trash2, Wrench } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { orderConfirmationAPI, customerAPI, productAPI, branchAPI, quotationAPI, billingAddressAPI, savedAddressAPI, fetchPaged, unwrapItems, ocAmendmentAPI, siteProjectAPI, stockAPI, salespersonAPI, productionAPI } from '../../services/api';
import api from '../../services/api';
import { filterBranchesForUser } from '../../utils/branchScope';
import { getAllowedUOMs, convertRate } from '../../utils/uomConversion';
import { usePermissions } from '../../hooks/usePermissions';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import ProductSearchInput from '../ui/ProductSearchInput';
import CustomerSearchInput from '../ui/CustomerSearchInput';

import OCDetailView from './OCDetailView';
import OCListView from './OCListView';
import { OCDispatchDialog, OCInvoiceDialog, OCAmendDialog } from './OCDialogs';
import { isSqmMode, sqmLineDisplay } from './signageSqmUtils';

const freightOptions = [
  'FOR Destination (included in price)',
  'FOR Destination (extra at actual)',
  'Ex-Works (buyer to arrange pickup)',
  'To Pay (freight paid by buyer)',
];

const emptyItem = { product_name: '', description: '', hsn_code: '', qty: 1, unit: 'PCS', rate: 0, discount: 0, amount: 0, _product_id: '', _locked_unit: '' };

const OrderConfirmationModule = () => {
  const [ocs, setOcs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [billingAddresses, setBillingAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);  // Sync double-click guard (prevents duplicate OCs)
  const [search, setSearch] = useState('');
  const [view, setView] = useState('list');
  const [selectedOC, setSelectedOC] = useState(null);
  const [sourceMode, setSourceMode] = useState(null);
  // Convert-Quotation-to-OC search (server-side, covers all quotations not just first page)
  const [quoteSearch, setQuoteSearch] = useState('');
  const [quoteSearchResults, setQuoteSearchResults] = useState(null);
  const [quoteSearching, setQuoteSearching] = useState(false);
  const quoteSearchDebounce = useRef();
  const printRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('prismoline_user') || '{}');
  const { hasPermission } = usePermissions();
  const canMarkDispatch = hasPermission('oc.ready_to_dispatch');
  const canAddInvoice = hasPermission('oc.add_invoice');
  const canBulkUpdate = hasPermission('oc.bulk_status');
  const canAmend = hasPermission('oc.amend');
  const canConfirmDispatch = hasPermission('oc.confirm_dispatch');

  // Dispatch dialog
  const [showDispatchDialog, setShowDispatchDialog] = useState(false);
  const [dispatchOC, setDispatchOC] = useState(null);
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [dispatchNotes, setDispatchNotes] = useState('');
  const [selectedReadyItems, setSelectedReadyItems] = useState(new Set());
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const [dispatchQtys, setDispatchQtys] = useState({});
  const [logoBase64, setLogoBase64] = useState('');
  const [transporterName, setTransporterName] = useState('');
  const [driverName, setDriverName] = useState('');
  const [driverPhone, setDriverPhone] = useState('');
  const [lrNumber, setLrNumber] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('');

  // Admin: Product-Binding diagnostics (heals stale product_id refs that cause
  // "I have stock but OC says I don't" → the real root cause behind today's
  // SNOW_TRM1400 false-zero at Ranchi HQ).
  const isAdmin = (user.role || '').toLowerCase() === 'admin';
  const [showBindingDialog, setShowBindingDialog] = useState(false);
  const [bindingReport, setBindingReport] = useState(null);
  const [bindingLoading, setBindingLoading] = useState(false);
  const [healingActive, setHealingActive] = useState(false);
  const openBindingDialog = async () => {
    setShowBindingDialog(true);
    setBindingLoading(true);
    setBindingReport(null);
    try {
      const r = await orderConfirmationAPI.productBindingReport(true);
      setBindingReport(r);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to scan OC bindings');
    } finally {
      setBindingLoading(false);
    }
  };
  const runHeal = async (apply) => {
    setHealingActive(true);
    try {
      const r = await orderConfirmationAPI.productBindingHeal({ dry_run: !apply });
      toast.success(apply
        ? `Healed ${r.fixed} item binding(s) across affected OCs. Skipped ${r.skipped_unfixable} unresolvable case(s).`
        : `Dry-run: ${r.fixed} item(s) would be fixed, ${r.skipped_unfixable} need manual review.`);
      if (apply) {
        // Refresh the report so the dialog reflects the current state
        const fresh = await orderConfirmationAPI.productBindingReport(true);
        setBindingReport(fresh);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Heal action failed');
    } finally {
      setHealingActive(false);
    }
  };
  const [confirmDispatchLoading, setConfirmDispatchLoading] = useState(false);

  // Invoice dialog
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [invoiceOC, setInvoiceOC] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [invoiceDocUrl, setInvoiceDocUrl] = useState('');
  const [invoiceDocName, setInvoiceDocName] = useState('');
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  // Amendment
  const [showAmendDialog, setShowAmendDialog] = useState(false);
  const [amendOC, setAmendOC] = useState(null);
  const [amendItems, setAmendItems] = useState([]);
  const [amendReason, setAmendReason] = useState('');
  const [amendDetails, setAmendDetails] = useState({});
  const [amendLoading, setAmendLoading] = useState(false);
  const [amendments, setAmendments] = useState([]);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [editDraftMode, setEditDraftMode] = useState(false);

  // Address book
  const [savedShipTo, setSavedShipTo] = useState([]);
  const [customerShipToAddresses, setCustomerShipToAddresses] = useState([]);
  const [savedConsignee, setSavedConsignee] = useState([]);
  const [creditCheck, setCreditCheck] = useState(null);
  const [siteProjects, setSiteProjects] = useState([]);
  const [salespersons, setSalespersons] = useState([]);

  // Shortfall modal
  const [showShortfallModal, setShowShortfallModal] = useState(false);
  const [shortfallItems, setShortfallItems] = useState([]);
  const [shortfallCreating, setShortfallCreating] = useState(false);

  // Bulk
  const [selectedOCs, setSelectedOCs] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', order: 'desc' });
  const handlePageSizeChange = (v) => { setPageSize(v); setPage(1); };
  const handleSort = (key, order) => { setSortConfig({ key, order }); setPage(1); };

  const [form, setForm] = useState({
    customer: { id: '', name: '', address: '', city: '', state: '', pincode: '', gstin: '', pan: '', phone: '', email: '', contact_person: '' },
    billing_address: null, ship_to: null, ship_to_same: true, consignee: null, consignee_same: true,
    oc_type: 'project', project_name: '', site_project_id: '', site_project_code: '',
    items: [{ ...emptyItem }],
    signage_items: [],
    bom_items: [],
    other_charges: [],
    gst_rate: 18,
    terms: { payment_terms: '50% advance, 50% against delivery', delivery_schedule: 'Within 15 days from OC date', warranty: '12 months from date of application', freight: 'FOR Destination (included in price)', notes: '' },
    salesman_name: user.name || '', branch: user.branch || '', remarks: '',
  });

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [ocsResult, customersData, productsData, quotesData, billingData, shipToData, consigneeData, branchesData, projectsData, spData] = await Promise.all([
        fetchPaged('/order-confirmations', { page, page_size: pageSize, search: search || undefined, status: statusFilter !== 'all' ? statusFilter : undefined, branch: branchFilter || undefined, sort_by: sortConfig.key, sort_order: sortConfig.order }),
        customerAPI.getAll().catch(() => []),
        productAPI.getAll().catch(() => []),
        quotationAPI.getAll().catch(() => []),
        billingAddressAPI.getApprovedList().catch(() => []),
        savedAddressAPI.getByType('ship_to').catch(() => []),
        savedAddressAPI.getByType('consignee').catch(() => []),
        branchAPI.getAll().catch(() => []),
        siteProjectAPI.getDropdownList().catch(() => []),
        salespersonAPI.getAll().catch(() => []),
      ]);
      setOcs(ocsResult.items); setTotalItems(ocsResult.total); setTotalPages(ocsResult.total_pages);
      setCustomers(customersData); setProducts(productsData); setQuotes(quotesData);
      setBillingAddresses(billingData); setSavedShipTo(shipToData); setSavedConsignee(consigneeData);
      setBranches(branchesData); setSiteProjects(projectsData); setSalespersons(spData);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [page, search, statusFilter, branchFilter, pageSize, sortConfig]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    fetch('/prismoline-logo.png')
      .then(r => r.blob())
      .then(blob => { const reader = new FileReader(); reader.onloadend = () => setLogoBase64(reader.result); reader.readAsDataURL(blob); })
      .catch(() => {});
  }, []);

  // Handle deep-link from ?oc= param (e.g., from Stock History dispatch link)
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ocId = params.get('oc');
    if (ocId) {
      orderConfirmationAPI.getById(ocId).then(oc => {
        setSelectedOC(oc);
        setView('detail');
      }).catch(() => {});
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.search, location.pathname]);

  // === Dispatch ===
  const openDispatchDialog = async (oc) => {
    // Run stock check before opening dispatch dialog — block if stock insufficient (for ALL branches)
    const isSignage = (oc.oc_type === 'signage') || (oc.signage_items && oc.signage_items.length > 0);
    const allItems = (oc.items && oc.items.length > 0) ? oc.items : (oc.signage_items || []);
    const branch = oc.billing_address?.branch_id || oc.branch || '';
    if (branch && allItems.length > 0) {
      try {
        toast.info('Checking stock availability...');
        // For signage OCs, use oc_id so backend can resolve signage_items → FG products
        const payload = isSignage
          ? { branch, oc_id: oc.id }
          : {
              branch,
              items: allItems.map(item => ({
                product_name: item.product_name || '',
                _product_id: item.product_id || item._product_id || '',
                product_id: item.product_id || item._product_id || '',
                qty: item.qty || 0,
                unit: item.unit || 'PCS',
              }))
            };
        const result = await orderConfirmationAPI.stockCheck(payload);
        if (!result.all_sufficient) {
          const shortItems = (result.items || []).filter(i => !i.sufficient);
          const details = shortItems.map(i => {
            let main = `${i.product_name}: need ${i.required_qty}, available ${i.available_qty} ${i.unit}`;
            if ((i.reserved_awaiting_invoice || 0) > 0) {
              main += `\n   (physical ${i.physical_balance} − ${i.reserved_awaiting_invoice} reserved by pending Awaiting-Invoice dispatches)`;
            }
            const others = (i.available_at_other_branches || []).slice(0, 3)
              .map(b => `${b.branch_name} (${b.available_qty})`).join(', ');
            return others ? `${main}\n   → Available at: ${others}` : main;
          }).join('\n');
          const anyOther = shortItems.some(i => (i.available_at_other_branches || []).length > 0);
          toast.error(`Cannot dispatch — insufficient stock at ${result.branch_name || 'branch'}`, { duration: 6000 });
          alert(
            `Insufficient stock at ${result.branch_name || 'branch'}:\n\n${details}\n\n` +
            (anyOther
              ? 'Stock is available at other branches. Create a Stock Transfer Note to move it first.\nTip: open the OC detail and click "Create STN Draft".'
              : 'Please arrange stock (via production, inward, or branch transfer) before dispatching.')
          );
          return;
        }
      } catch (err) {
        console.error('Stock check failed:', err);
      }
    }

    setDispatchOC(oc);
    setSelectedReadyItems(new Set(allItems.map((_, i) => i)));
    const qtys = {};
    allItems.forEach((item, i) => { qtys[i] = item.qty || 0; });
    setDispatchQtys(qtys);
    setVehicleNumber(oc.vehicle_number || '');
    setTransporterName(''); setDriverName(''); setDriverPhone(''); setLrNumber('');
    setDispatchNotes(''); setShowDispatchDialog(true);
  };

  const handleMarkReadyToDispatch = async () => {
    if (!vehicleNumber.trim()) { toast.error('Vehicle number is mandatory'); return; }
    if (selectedReadyItems.size === 0) { toast.error('Select at least one item to dispatch'); return; }
    setDispatchLoading(true);
    try {
      // Build dispatch_items with quantities
      const dispatchItemsData = Array.from(selectedReadyItems).map(idx => ({
        index: idx,
        dispatch_qty: dispatchQtys[idx] || 0,
      })).filter(d => d.dispatch_qty > 0);

      await orderConfirmationAPI.markReadyToDispatch(dispatchOC.id, {
        vehicle_number: vehicleNumber.trim(),
        transporter_name: transporterName,
        driver_name: driverName,
        driver_phone: driverPhone,
        lr_number: lrNumber,
        notes: dispatchNotes,
        dispatch_items: dispatchItemsData,
      });
      toast.success(`Dispatch Note created for ${dispatchOC.oc_number}`);
      setShowDispatchDialog(false); fetchAll();
      if (selectedOC?.id === dispatchOC.id) setSelectedOC(await orderConfirmationAPI.getById(dispatchOC.id));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create dispatch'); }
    setDispatchLoading(false);
  };

  // === Confirm Dispatch (the tick after invoice) ===
  const handleConfirmDispatch = async (ocId) => {
    if (!window.confirm('Confirm this dispatch? This will mark the OC as "Dispatched" (final status).')) return;
    setConfirmDispatchLoading(true);
    try {
      await orderConfirmationAPI.confirmDispatch(ocId);
      toast.success('Dispatch confirmed — OC marked as Dispatched');
      fetchAll();
      if (selectedOC?.id === ocId) setSelectedOC(await orderConfirmationAPI.getById(ocId));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to confirm dispatch'); }
    setConfirmDispatchLoading(false);
  };

  // === Invoice ===
  const openInvoiceDialog = (oc) => {
    setInvoiceOC(oc); setInvoiceNumber(oc.tally_invoice_number || '');
    setInvoiceDate(oc.tally_invoice_date || new Date().toISOString().slice(0, 10));
    setInvoiceNotes(oc.tally_invoice_notes || '');
    setInvoiceDocUrl(oc.invoice_document_url || '');
    setInvoiceDocName(oc.invoice_document_name || '');
    setShowInvoiceDialog(true);
  };

  const handleUpdateInvoice = async () => {
    if (!invoiceNumber.trim()) { toast.error('Invoice number is required'); return; }
    if (!invoiceDocUrl) { toast.error('Please upload the Tally invoice document'); return; }
    setInvoiceLoading(true);
    try {
      await orderConfirmationAPI.updateInvoice(invoiceOC.id, {
        invoice_number: invoiceNumber.trim(),
        invoice_date: invoiceDate,
        invoice_notes: invoiceNotes,
        invoice_document_url: invoiceDocUrl,
        invoice_document_name: invoiceDocName,
      });
      toast.success(`Invoice ${invoiceNumber} linked to ${invoiceOC.oc_number}`);
      setShowInvoiceDialog(false); fetchAll();
      if (selectedOC?.id === invoiceOC.id) setSelectedOC(await orderConfirmationAPI.getById(invoiceOC.id));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update invoice'); }
    setInvoiceLoading(false);
  };

  // === Confirm Draft ===
  const handleConfirmDraft = async (ocId) => {
    if (!window.confirm('Confirm this OC? It will move into the active workflow and can only be changed via amendment.')) return;
    setConfirmLoading(true);
    try {
      await orderConfirmationAPI.confirmDraft(ocId);
      toast.success('OC confirmed successfully'); fetchAll();
      if (selectedOC?.id === ocId) setSelectedOC(await orderConfirmationAPI.getById(ocId));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to confirm OC'); }
    setConfirmLoading(false);
  };

  // === Edit Draft ===
  const handleEditDraft = (oc) => {
    // Map items to ensure _product_id is set for ProductSearchInput
    const mappedItems = (oc.items || []).map(item => ({
      ...item,
      _product_id: item._product_id || item.product_id || '',
    }));
    const isSignage = oc.oc_type === 'signage' && (oc.signage_items || []).length > 0;
    setForm({
      customer: oc.customer || { id: '', name: '', address: '', city: '', state: '', pincode: '', gstin: '', pan: '', phone: '', email: '', contact_person: '' },
      billing_address: oc.billing_address || null, ship_to: oc.ship_to || null, ship_to_same: !oc.ship_to,
      consignee: oc.consignee || null, consignee_same: !oc.consignee,
      oc_type: oc.oc_type || 'project', project_name: oc.project_name || '',
      site_project_id: oc.site_project_id || '', site_project_code: oc.site_project_code || '',
      items: mappedItems.length ? mappedItems : [{ ...emptyItem }],
      signage_items: isSignage ? JSON.parse(JSON.stringify(oc.signage_items)) : [],
      bom_items: oc.bom_items ? JSON.parse(JSON.stringify(oc.bom_items)) : [],
      other_charges: oc.other_charges || [],
      gst_rate: oc.gst_rate || 18,
      terms: oc.terms || { payment_terms: '', delivery_schedule: '', warranty: '', freight: '', notes: '' },
      salesman_name: oc.salesman_name || '', branch: oc.branch || '', remarks: oc.remarks || '',
    });
    setEditDraftMode(oc.id); setView('create'); setSourceMode('direct');
  };

  const handleSaveDraftEdit = async () => {
    if (!form.customer.name) { alert('Please select a customer'); return; }
    try {
      setSubmitting(true);
      const payload = {
        customer: form.customer, oc_type: form.oc_type,
        other_charges: form.other_charges,
        subtotal, gst_rate: form.gst_rate, gst_amount: gstAmount,
        cgst, sgst, igst, is_intra_state: isIntraState, total,
        terms: form.terms, salesman_name: form.salesman_name, salesman_phone: form.salesman_phone || '', branch: form.branch,
        branch_name: branches.find(b => b.id === form.branch)?.name || form.branch,
        project_name: form.project_name, site_project_id: form.site_project_id,
        site_project_code: form.site_project_code, billing_address: form.billing_address,
        ship_to: form.ship_to_same ? null : form.ship_to,
        consignee: form.consignee_same ? null : form.consignee, remarks: form.remarks,
      };
      if (isSignageForm) {
        // Signage OC: keep signage_items + bom_items; leave items array untouched (usually empty)
        payload.signage_items = form.signage_items;
        payload.bom_items = form.bom_items;
      } else {
        payload.items = form.items.filter(i => i._product_id || i.product_id || (i.product_name && i.product_name.trim()) || Number(i.qty) > 0 || Number(i.rate) > 0);
      }
      await orderConfirmationAPI.editDraft(editDraftMode, payload);
      toast.success('Draft OC updated'); setEditDraftMode(false); setView('list'); setSourceMode(null); fetchAll();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to update draft'); }
    finally { setSubmitting(false); }
  };

  // === Amendment ===
  const openAmendDialog = (oc) => {
    setAmendOC(oc); setAmendItems(JSON.parse(JSON.stringify(oc.items || [])));
    setAmendDetails({
      ship_to: oc.ship_to ? JSON.parse(JSON.stringify(oc.ship_to)) : null,
      consignee: oc.consignee ? JSON.parse(JSON.stringify(oc.consignee)) : null,
      billing_address: oc.billing_address ? JSON.parse(JSON.stringify(oc.billing_address)) : null,
      terms: oc.terms ? JSON.parse(JSON.stringify(oc.terms)) : { payment_terms: '', delivery_schedule: '', warranty: '', freight: '', notes: '' },
      remarks: oc.remarks || '',
      other_charges: oc.other_charges ? JSON.parse(JSON.stringify(oc.other_charges)) : [],
    });
    setAmendReason(''); setShowAmendDialog(true);
  };

  const handleSubmitAmendment = async () => {
    if (!amendReason.trim()) { toast.error('Please provide a reason for the amendment'); return; }
    if (!amendItems.length) { toast.error('Items cannot be empty'); return; }
    setAmendLoading(true);
    try {
      const payload = { proposed_items: amendItems, reason: amendReason };
      // Include amended details
      if (amendDetails.ship_to) payload.ship_to = amendDetails.ship_to;
      if (amendDetails.consignee) payload.consignee = amendDetails.consignee;
      if (amendDetails.billing_address) payload.billing_address = amendDetails.billing_address;
      if (amendDetails.terms) payload.terms = amendDetails.terms;
      if (amendDetails.remarks !== undefined) payload.remarks = amendDetails.remarks;
      if (amendDetails.customer) payload.customer = amendDetails.customer;
      if (amendDetails.branch) payload.branch = amendDetails.branch;
      // Always send other_charges (could be empty array — preserves intent to clear all)
      payload.other_charges = amendDetails.other_charges || [];
      await ocAmendmentAPI.submit(amendOC.id, payload);
      toast.success('Amendment submitted for approval'); setShowAmendDialog(false);
      if (selectedOC?.id === amendOC.id) {
        const [updatedOC, amds] = await Promise.all([orderConfirmationAPI.getById(amendOC.id), ocAmendmentAPI.getForOC(amendOC.id)]);
        setSelectedOC(updatedOC); setAmendments(amds);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit amendment'); }
    setAmendLoading(false);
  };

  const fetchAmendments = async (ocId) => {
    try { setAmendments(await ocAmendmentAPI.getForOC(ocId)); } catch { setAmendments([]); }
  };

  // === Form helpers ===
  const selectCustomer = (c) => {
    if (c) {
      const spId = c.salesperson_id || '';
      const sp = salespersons.find(s => s.id === spId);
      setForm(prev => ({
        ...prev,
        customer: { id: c.id, name: c.name, address: c.address || '', city: c.city || '', state: c.state || '', pincode: c.pincode || '', gstin: c.gstin || '', pan: c.pan || '', phone: c.phone || '', email: c.email || '', contact_person: c.contact || '' },
        ...(spId ? { salesman_name: sp?.name || c.salesperson_name || '', salesman_phone: sp?.phone || '' } : {}),
      }));
      customerAPI.creditCheck(c.id).then(data => setCreditCheck(data)).catch(() => setCreditCheck(null));
      // Fetch customer-specific shipping addresses
      customerAPI.getShippingAddresses(c.id).then(addrs => setCustomerShipToAddresses(addrs || [])).catch(() => setCustomerShipToAddresses([]));
    }
  };

  const clearCustomer = () => {
    setForm(prev => ({ ...prev, customer: { id: '', name: '', address: '', city: '', state: '', pincode: '', gstin: '', pan: '', phone: '', email: '', contact_person: '' } }));
    setCreditCheck(null);
    setCustomerShipToAddresses([]);
  };

  const [productSearches, setProductSearches] = useState({});
  const selectProduct = (idx, productId) => {
    const p = products.find(x => x.id === productId);
    if (p) _applyOCProduct(idx, p);
  };

  const handleOCProductSelect = (idx, p) => {
    _applyOCProduct(idx, p);
  };

  const _applyOCProduct = (idx, p) => {
    const items = [...form.items];
    const rawUnit = p.sell_unit || p.unit || 'PCS';
    const normalizedUnit = rawUnit && rawUnit !== 'None' ? rawUnit.toUpperCase() : 'PCS';
    const conversions = p.unit_conversions || p.conversions || [];
    const baseUnit = p.unit || 'PCS';
    const baseRate = p.selling_price || 0;
    // Auto-convert rate if sell_unit differs from base unit
    let rate = baseRate;
    if (normalizedUnit !== baseUnit.toUpperCase() && conversions.length > 0) {
      rate = convertRate(conversions, baseUnit, baseRate, rawUnit);
    }
    items[idx] = {
      ...items[idx], product_name: p.name, hsn_code: p.hsn_code || '', unit: normalizedUnit,
      rate, _product_id: p.id, sku: p.sku || '',
      _allowedUOMs: getAllowedUOMs(p), _baseUnit: baseUnit, _baseRate: baseRate, _conversions: conversions,
    };
    items[idx].amount = Math.round(items[idx].qty * items[idx].rate * (1 - items[idx].discount / 100));
    setForm(prev => ({ ...prev, items })); setProductSearches(prev => ({ ...prev, [idx]: undefined }));
  };

  const handleOCProductClear = (idx) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], product_name: '', hsn_code: '', unit: 'PCS', rate: 0, amount: 0, _product_id: '', _locked_unit: '' };
    setForm(prev => ({ ...prev, items })); setProductSearches(prev => ({ ...prev, [idx]: undefined }));
  };

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    // Auto-convert rate when unit changes
    if (field === 'unit' && items[idx]._conversions?.length > 0) {
      const newRate = convertRate(items[idx]._conversions, items[idx]._baseUnit || '', items[idx]._baseRate || 0, value);
      items[idx].rate = newRate;
    }
    if (['qty', 'rate', 'discount', 'unit'].includes(field)) {
      const q = Number(items[idx].qty) || 0, r = Number(items[idx].rate) || 0, d = Number(items[idx].discount) || 0;
      items[idx].amount = Math.round(q * r * (1 - d / 100));
    }
    setForm(prev => ({ ...prev, items }));
  };

  // Signage board editor — only allows light edits (qty/rate/description).
  // Shape, dimensions, materials and cost-mode stay locked because they
  // drive the auto-created FG Product, BOM and Production Orders.
  const updateSignageItem = (idx, field, value) => {
    const sigs = [...form.signage_items];
    sigs[idx] = { ...sigs[idx], [field]: value };
    const si = sigs[idx];
    // If user edits qty or rate, switch to per-pcs pricing mode and recompute total_amount
    if (field === 'qty' || field === 'custom_per_pcs_rate') {
      const qty = Number(si.qty) || 0;
      // Effective rate = overridden per-pcs rate OR original computed rate
      const rate = field === 'custom_per_pcs_rate'
        ? Number(value) || 0
        : (Number(si.custom_per_pcs_rate) || Number(si.final_per_board) || 0);
      if (field === 'custom_per_pcs_rate') {
        si.pricing_mode = 'per_pcs';
      }
      si.total_amount = Math.round(qty * rate);
    }
    setForm(prev => ({ ...prev, signage_items: sigs }));
  };

  const isSignageForm = form.oc_type === 'signage' && (form.signage_items || []).length > 0;
  const subtotal = isSignageForm
    ? form.signage_items.reduce((s, si) => s + (Number(si.total_amount) || 0), 0)
    : form.items.reduce((s, i) => s + (i.amount || 0), 0);
  const chargesTotal = (form.other_charges || []).reduce((s, c) => s + (c.amount || 0), 0);
  const taxableCharges = (form.other_charges || []).filter(c => c.taxable !== false).reduce((s, c) => s + (c.amount || 0), 0);
  const shipFromState = form.billing_address?.state || '';
  const customerState = form.customer.state || '';
  const isIntraState = shipFromState && customerState ? shipFromState.toLowerCase() === customerState.toLowerCase() : false;
  const gstAmount = Math.round((subtotal + taxableCharges) * form.gst_rate / 100);
  const cgst = isIntraState ? Math.round(gstAmount / 2) : 0;
  const sgst = isIntraState ? gstAmount - cgst : 0;
  const igst = isIntraState ? 0 : gstAmount;
  const total = subtotal + chargesTotal + gstAmount;

  // Stock check
  const [stockCheck, setStockCheck] = useState(null);
  const [stockCheckLoading, setStockCheckLoading] = useState(false);

  // Branch-wise stock per product — uses unified movement formula (matches Item History)
  const [branchStock, setBranchStock] = useState({});
  const [branchStockDetails, setBranchStockDetails] = useState({}); // { pid: {physical_balance, reserved_awaiting_invoice, available} }
  useEffect(() => {
    if (!form.branch) { setBranchStock({}); setBranchStockDetails({}); return; }
    const productIds = form.items.map(i => i._product_id).filter(Boolean);
    if (!productIds.length) return;
    const fetchStock = async () => {
      try {
        const data = await stockAPI.batchNetQty(form.branch, productIds);
        setBranchStock(data.stock || {});
        setBranchStockDetails(data.details || {});
      } catch {
        setBranchStock({});
        setBranchStockDetails({});
      }
    };
    fetchStock();
  }, [form.branch, form.items.map(i => i._product_id).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Invalidate cached stock-check whenever branch or items change — prevents stale "insufficient stock"
  // errors when the user has corrected the form after a prior failed save attempt.
  useEffect(() => {
    setStockCheck(null);
  }, [form.branch, form.items.map(i => `${i._product_id}:${i.qty}`).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateDirect = async () => {
    if (submittingRef.current) return; // Sync guard: prevents double-click duplicate OCs
    if (!form.customer.name) { alert('Please select a customer'); return; }
    if (!form.salesman_name) { alert('Please select a salesman'); return; }
    if (form.items.length === 0 || !form.items[0].product_name) { alert('Add at least one item'); return; }
    // CRITICAL: Set the sync guard BEFORE the slow async stockCheck below.
    // Earlier the guard was set after stockCheck, so rapid double/triple-clicks fired
    // multiple parallel stockChecks → multiple parallel creates → duplicate OCs.
    submittingRef.current = true;
    setSubmitting(true);
    // ALWAYS fetch a fresh stock check on every save click — never trust cached results.
    // Prevents stale "insufficient stock" errors when data has changed since the last click.
    if (form.branch) {
      setStockCheckLoading(true);
      try {
        const r = await orderConfirmationAPI.stockCheck({ branch: form.branch, items: form.items });
        setStockCheck({ ...r, _checkedAt: new Date().toLocaleTimeString() });
        if (!r.can_proceed) { setStockCheckLoading(false); setSubmitting(false); submittingRef.current = false; return; }
        if (!r.all_sufficient && r.is_hq) {
          setStockCheckLoading(false);
          // Show shortfall modal instead of window.confirm
          const deficits = r.items.filter(i => !i.sufficient).map(i => ({
            product_name: i.product_name,
            product_id: form.items.find(fi => (fi.product_name || '').trim() === (i.product_name || '').trim())?._product_id || '',
            required_qty: i.required_qty,
            available_qty: i.available_qty,
            shortfall_qty: i.shortfall,
            unit: i.unit,
          }));
          setShortfallItems(deficits);
          setShowShortfallModal(true);
          // Release guard so the user can pick an action from the modal (the modal
          // handlers have their own guard)
          setSubmitting(false); submittingRef.current = false;
          return;
        }
        if (!r.all_sufficient && !r.is_hq) {
          // Non-HQ branch insufficient stock → show red banner and stop (user needs STN first)
          setStockCheckLoading(false);
          setSubmitting(false); submittingRef.current = false;
          return;
        }
      } catch (e) { console.error(e); setStockCheckLoading(false); setSubmitting(false); submittingRef.current = false; return; }
      finally { setStockCheckLoading(false); }
    }
    if (creditCheck?.exceeded) { if (!window.confirm(`Warning: ${creditCheck.customer_name}'s credit limit exceeded.\nOutstanding: INR ${creditCheck.outstanding?.toLocaleString()}\n\nProceed?`)) { setSubmitting(false); submittingRef.current = false; return; } }
    try {
      await orderConfirmationAPI.create({
        customer: form.customer, oc_type: form.oc_type || 'project', source: 'direct',
        items: form.items.filter(i => i._product_id || i.product_id || (i.product_name && i.product_name.trim()) || Number(i.qty) > 0 || Number(i.rate) > 0).map(i => ({ ...i, product_id: i._product_id || i.product_id || '' })), signage_items: [], bom_items: [],
        other_charges: form.other_charges || [],
        subtotal, gst_rate: form.gst_rate, gst_amount: gstAmount, cgst, sgst, igst, is_intra_state: isIntraState, total,
        terms: form.terms, salesman_name: form.salesman_name, salesman_phone: form.salesman_phone || '', branch: form.branch,
        branch_name: branches.find(b => b.id === form.branch)?.name || form.branch,
        project_name: form.project_name, site_project_id: form.site_project_id,
        site_project_code: form.site_project_code,
        billing_address: form.billing_address, ship_to: form.ship_to_same ? null : form.ship_to,
        consignee: form.consignee_same ? null : form.consignee, remarks: form.remarks,
      });
      setView('list'); setSourceMode(null); setStockCheck(null); fetchAll();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to create OC'); }
    finally { setSubmitting(false); submittingRef.current = false; }
  };

  const handleCreateFromQuote = async (quoteId) => {
    if (submittingRef.current) return; // Sync guard: prevents double-click duplicate OCs
    submittingRef.current = true;
    try { setSubmitting(true); await orderConfirmationAPI.createFromQuotation(quoteId); setView('list'); setSourceMode(null); fetchAll(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed to convert quotation'); }
    finally { setSubmitting(false); submittingRef.current = false; }
  };

  // Server-side search of ALL quotations for the Convert-to-OC screen (not just the first page)
  const handleQuoteSearch = (val) => {
    setQuoteSearch(val);
    clearTimeout(quoteSearchDebounce.current);
    const term = val.trim();
    if (!term) {
      setQuoteSearchResults(null); // fall back to the initially-loaded list
      setQuoteSearching(false);
      return;
    }
    setQuoteSearching(true);
    quoteSearchDebounce.current = setTimeout(async () => {
      try {
        const data = await quotationAPI.getAll({ search: term, page: 1, page_size: 50 });
        setQuoteSearchResults(Array.isArray(data) ? data : (data?.items || []));
      } catch {
        setQuoteSearchResults([]);
      } finally {
        setQuoteSearching(false);
      }
    }, 350);
  };

  // === Shortfall modal: create OC (shared logic) ===
  const createOCInternal = async () => {
    const oc = await orderConfirmationAPI.create({
      customer: form.customer, oc_type: form.oc_type || 'project', source: 'direct',
      items: form.items.filter(i => i._product_id || i.product_id || (i.product_name && i.product_name.trim()) || Number(i.qty) > 0 || Number(i.rate) > 0).map(i => ({ ...i, product_id: i._product_id || i.product_id || '' })), signage_items: [], bom_items: [],
      other_charges: form.other_charges || [],
      subtotal, gst_rate: form.gst_rate, gst_amount: gstAmount, cgst, sgst, igst, is_intra_state: isIntraState, total,
      terms: form.terms, salesman_name: form.salesman_name, branch: form.branch,
      branch_name: branches.find(b => b.id === form.branch)?.name || form.branch,
      project_name: form.project_name, site_project_id: form.site_project_id,
      site_project_code: form.site_project_code,
      billing_address: form.billing_address, ship_to: form.ship_to_same ? null : form.ship_to,
      consignee: form.consignee_same ? null : form.consignee, remarks: form.remarks,
    });
    return oc;
  };

  const handleShortfallCreateOCOnly = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      setShortfallCreating(true);
      await createOCInternal();
      setShowShortfallModal(false);
      setShortfallItems([]);
      setView('list'); setSourceMode(null); setStockCheck(null); fetchAll();
      toast.success('Order Confirmation created (without production orders)');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create OC'); }
    finally { setShortfallCreating(false); submittingRef.current = false; }
  };

  const handleShortfallCreateWithProduction = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      setShortfallCreating(true);
      const oc = await createOCInternal();
      // Auto-create production orders for shortfall items
      const validItems = shortfallItems.filter(i => i.shortfall_qty > 0);
      if (validItems.length > 0) {
        const result = await productionAPI.autoFromShortfall({
          oc_id: oc.id, oc_number: oc.oc_number, branch: form.branch,
          items: validItems,
        });
        toast.success(`OC created + ${result.created} production order(s) auto-created`);
      } else {
        toast.success('Order Confirmation created');
      }
      setShowShortfallModal(false);
      setShortfallItems([]);
      setView('list'); setSourceMode(null); setStockCheck(null); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setShortfallCreating(false); submittingRef.current = false; }
  };

  // === PDF ===
  const generateOCPdf = (oc, includeBOM = false) => {
    const pdf = new jsPDF(); const pw = 210; const m = 15; let y = 12;
    const spPhone = oc.salesman_phone || '';
    const contactLine = `www.prismoline.com | sales@prismoline.com${spPhone ? ' | +91 ' + spPhone : ''}`;
    pdf.setFillColor(36, 84, 156); pdf.rect(0, 0, pw, 38, 'F');
    pdf.setTextColor(255); pdf.setFontSize(18); pdf.setFont(undefined, 'bold');
    pdf.text('SHARDA INFRASOLUTIONS PVT. LTD.', m, 14);
    pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
    pdf.text('Road Safety & Infrastructure Solutions', m, 22);
    pdf.setFontSize(7);
    pdf.text('CIN: U24100JH2010PTC014546 | PAN: AAPCS1562H', m, 28);
    pdf.text(contactLine, m, 34);
    if (logoBase64) { try { pdf.addImage(logoBase64, 'PNG', pw - m - 40, 4, 38, 28); } catch (e) { /* */ } }
    y = 44;
    pdf.setTextColor(36, 84, 156); pdf.setFontSize(13); pdf.setFont(undefined, 'bold');
    pdf.text('ORDER CONFIRMATION', pw / 2, y, { align: 'center' }); y += 8;
    pdf.setDrawColor(36, 84, 156); pdf.line(m, y, pw - m, y); y += 6;
    pdf.setTextColor(0); pdf.setFontSize(8); pdf.setFont(undefined, 'bold');
    pdf.text(`OC No: ${oc.oc_number}`, m, y); pdf.text(`Date: ${new Date(oc.created_at).toLocaleDateString('en-IN')}`, pw / 2, y); y += 5;
    if (oc.project_name) { pdf.text(`Project: ${oc.project_name}`, m, y); y += 5; }
    pdf.text(`Salesman: ${oc.salesman_name}`, m, y); y += 8;
    // Bill To / Ship From — 2-box layout with dynamic height, no overlap
    const halfW = (pw - 2 * m - 4) / 2;
    const custAddrParts = [oc.customer?.address, oc.customer?.city, oc.customer?.state, oc.customer?.pincode ? `- ${oc.customer.pincode}` : ''].filter(Boolean).join(', ');
    const custAddrLines = custAddrParts ? pdf.splitTextToSize(custAddrParts, halfW - 8) : [];
    let custBoxH = 16 + custAddrLines.length * 3.5;
    if (oc.customer?.gstin) custBoxH += 5;
    if (oc.customer?.phone || oc.customer?.email) custBoxH += 4;

    const sfBranch = oc.billing_address?.name || '';
    const sfAddrParts = sfBranch ? [oc.billing_address.address, oc.billing_address.city, oc.billing_address.state, oc.billing_address.pincode ? `- ${oc.billing_address.pincode}` : ''].filter(Boolean).join(', ') : '';
    const sfAddrLines = sfAddrParts ? pdf.splitTextToSize(sfAddrParts, halfW - 8) : [];
    let sfBoxH = sfBranch ? (22 + sfAddrLines.length * 3.5 + (oc.billing_address?.gstin ? 5 : 0)) : 10;

    const bxH = Math.max(custBoxH, sfBoxH) + 4;

    // Box 1: BILL TO
    pdf.setFillColor(245, 247, 250); pdf.rect(m, y, halfW, bxH, 'F');
    pdf.setTextColor(36, 84, 156); pdf.setFontSize(7); pdf.setFont(undefined, 'bold');
    pdf.text('BILL TO:', m + 3, y + 5);
    pdf.setTextColor(0); pdf.setFontSize(8); pdf.setFont(undefined, 'bold');
    pdf.text(oc.customer?.name || '', m + 3, y + 11);
    pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
    let cY = y + 16;
    custAddrLines.forEach((l, i) => { pdf.text(l, m + 3, cY); cY += 3.5; });
    if (oc.customer?.gstin) { pdf.text(`GSTIN: ${oc.customer.gstin}`, m + 3, cY + 1); cY += 4; }
    if (oc.customer?.phone) { pdf.text(`Ph: ${oc.customer.phone}`, m + 3, cY + 1); }
    if (oc.customer?.email) { pdf.text(`Email: ${oc.customer.email}`, m + 40, cY + 1); }

    // Box 2: SHIP FROM (Branch)
    const box2X = m + halfW + 4;
    pdf.setFillColor(245, 247, 250); pdf.rect(box2X, y, halfW, bxH, 'F');
    pdf.setTextColor(36, 84, 156); pdf.setFontSize(7); pdf.setFont(undefined, 'bold');
    pdf.text('SHIP FROM:', box2X + 3, y + 5);
    pdf.setTextColor(0); pdf.setFontSize(8); pdf.setFont(undefined, 'bold');
    pdf.text('Sharda Infrasolutions Pvt Ltd', box2X + 3, y + 11);
    if (sfBranch) {
      pdf.setFontSize(7.5); pdf.text(sfBranch, box2X + 3, y + 16);
      pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
      let sY = y + 21;
      sfAddrLines.forEach((l, i) => { pdf.text(l, box2X + 3, sY); sY += 3.5; });
      if (oc.billing_address?.gstin) { pdf.text(`GSTIN: ${oc.billing_address.gstin}`, box2X + 3, sY + 1); }
    }
    y += bxH + 4;
    if (oc.ship_to?.name) { const sa = [oc.ship_to.address, oc.ship_to.city, oc.ship_to.state, oc.ship_to.pincode].filter(Boolean).join(', '); const stl = sa ? pdf.splitTextToSize(sa, pw - 2 * m - 6) : []; const stH = 15 + stl.length * 3.5; pdf.setFillColor(250, 250, 252); pdf.rect(m, y, pw - 2 * m, stH, 'F'); pdf.setTextColor(100); pdf.setFontSize(7); pdf.setFont(undefined, 'bold'); pdf.text('SHIP TO (DELIVERY ADDRESS):', m + 3, y + 5); pdf.setTextColor(0); pdf.setFontSize(8); pdf.setFont(undefined, 'bold'); pdf.text(oc.ship_to.name, m + 3, y + 10); pdf.setFont(undefined, 'normal'); pdf.setFontSize(7); stl.forEach((l, i) => pdf.text(l, m + 3, y + 14.5 + i * 3.5)); if (oc.ship_to.contact_person) pdf.text(`Contact: ${oc.ship_to.contact_person} ${oc.ship_to.phone || ''}`, pw / 2 + 15, y + 5); y += stH + 4; }
    if (oc.consignee?.name) { const ca = [oc.consignee.address, oc.consignee.city, oc.consignee.state, oc.consignee.pincode].filter(Boolean).join(', '); const cl = ca ? pdf.splitTextToSize(ca, pw - 2 * m - 6) : []; const cH = 15 + cl.length * 3.5; pdf.setFillColor(255, 252, 240); pdf.rect(m, y, pw - 2 * m, cH, 'F'); pdf.setTextColor(100); pdf.setFontSize(7); pdf.setFont(undefined, 'bold'); pdf.text('CONSIGNEE (RECEIVING AUTHORITY):', m + 3, y + 5); pdf.setTextColor(0); pdf.setFontSize(8); pdf.setFont(undefined, 'bold'); pdf.text(oc.consignee.name, m + 3, y + 10); pdf.setFont(undefined, 'normal'); pdf.setFontSize(7); cl.forEach((l, i) => pdf.text(l, m + 3, y + 14.5 + i * 3.5)); if (oc.consignee.gstin) pdf.text(`GSTIN: ${oc.consignee.gstin}`, pw / 2 + 15, y + 5); if (oc.consignee.contact_person) pdf.text(`Contact: ${oc.consignee.contact_person} ${oc.consignee.phone || ''}`, pw / 2 + 15, y + 10); y += cH + 4; }
    y += 4;
    const isSignage = oc.oc_type === 'signage' && oc.signage_items?.length > 0;
    if (isSignage) {
      // Header band — tall enough to fully contain the bold text
      pdf.setFillColor(36, 84, 156); pdf.rect(m, y, pw - 2 * m, 9, 'F'); pdf.setTextColor(255); pdf.setFontSize(7); pdf.setFont(undefined, 'bold');
      const headerX = [m + 2, m + 8, m + 88, m + 108, m + 132, m + 148, m + 165];
      ['#', 'Description', 'Shape', 'Dimensions', 'Qty', 'Rate', 'Amount'].forEach((h, i) => {
        pdf.text(h, headerX[i], y + 6);
      });
      y += 11;
      pdf.setTextColor(0); pdf.setFont(undefined, 'normal');
      const tw = pw - 2 * m;
      (oc.signage_items || []).forEach((item, idx) => {
        const effectiveRate = Number(item.custom_per_pcs_rate) || Number(item.per_board_rate) || Number(item.final_per_board) || 0;
        const descLines = pdf.splitTextToSize(item.description || '-', 78);
        const rowH = Math.max(6, descLines.length * 3.5 + 2);
        if (idx % 2 === 0) { pdf.setFillColor(248, 249, 252); pdf.rect(m, y - 4, tw, rowH, 'F'); }
        pdf.setTextColor(0);
        pdf.text(`${idx + 1}`, m + 2, y);
        pdf.text(descLines, m + 8, y);
        pdf.text(`${item.shape || '-'}`, m + 88, y);
        pdf.text(`${item.dimensions || '-'}`, m + 108, y);
        if (isSqmMode(item)) {
          const { totalSqm, ratePerSqm } = sqmLineDisplay(item, item.qty, Number(item.total_amount) || 0);
          pdf.text(`${totalSqm} SQM`, m + 132, y);
          pdf.text(`${ratePerSqm.toLocaleString('en-IN')}`, m + 148, y);
        } else {
          pdf.text(`${item.qty || ''}`, m + 132, y);
          pdf.text(`${effectiveRate.toLocaleString('en-IN')}`, m + 148, y);
        }
        pdf.text(`${(item.total_amount || 0).toLocaleString('en-IN')}`, m + 165, y);
        y += rowH;
        pdf.setDrawColor(220); pdf.line(m, y - 1, m + tw, y - 1);
        if (y > 260) { pdf.addPage(); y = 15; }
      });
    } else {
      pdf.setFillColor(36, 84, 156); pdf.rect(m, y, pw - 2 * m, 7, 'F'); pdf.setTextColor(255); pdf.setFontSize(7); pdf.setFont(undefined, 'bold');
      ['#', 'Product', 'HSN', 'Qty', 'Unit', 'Rate', 'Disc%', 'Amount'].forEach((h, i) => { pdf.text(h, [m + 2, m + 10, m + 60, m + 82, m + 95, m + 108, m + 125, m + 140][i], y + 5); }); y += 12;
      pdf.setTextColor(0); pdf.setFont(undefined, 'normal');
      (oc.items || []).forEach((item, idx) => { const name = item.product_name?.length > 30 ? item.product_name.substring(0, 30) + '...' : item.product_name || ''; pdf.text(`${idx + 1}`, m + 2, y); pdf.text(name, m + 10, y); pdf.text(item.hsn_code || '', m + 60, y); pdf.text(`${item.qty || ''}`, m + 82, y); pdf.text(item.unit || '', m + 95, y); pdf.text(`${(item.rate || 0).toLocaleString()}`, m + 108, y); pdf.text(`${item.discount || 0}%`, m + 125, y); pdf.text(`${(item.amount || 0).toLocaleString()}`, m + 140, y); y += 6; if (y > 265) { pdf.addPage(); y = 15; } });
    }
    y += 3; pdf.setDrawColor(200); pdf.line(m, y, pw - m, y); y += 6; pdf.setFontSize(8);
    pdf.text('Subtotal:', m + 100, y); pdf.setFont(undefined, 'bold'); pdf.text(`INR ${(oc.subtotal || 0).toLocaleString()}`, m + 135, y); y += 6;
    pdf.setFont(undefined, 'normal'); pdf.text(`GST (${oc.gst_rate || 18}%):`, m + 100, y); pdf.text(`INR ${(oc.gst_amount || 0).toLocaleString()}`, m + 135, y); y += 6;
    pdf.setFillColor(36, 84, 156); pdf.rect(m + 95, y - 4, pw - m - (m + 95), 8, 'F');
    pdf.setTextColor(255); pdf.setFont(undefined, 'bold'); pdf.setFontSize(9);
    pdf.text('Total:', m + 100, y + 1); pdf.text(`INR ${(oc.total || 0).toLocaleString()}`, m + 135, y + 1); y += 14;
    pdf.setTextColor(36, 84, 156); pdf.setFontSize(8); pdf.setFont(undefined, 'bold');
    pdf.text('Terms & Conditions', m, y); y += 6;
    pdf.setTextColor(60); pdf.setFontSize(7); pdf.setFont(undefined, 'normal');
    Object.entries(oc.terms || {}).forEach(([key, val]) => { if (!val) return; const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()); pdf.setFont(undefined, 'bold'); pdf.text(`${label}: `, m, y); pdf.setFont(undefined, 'normal'); pdf.text(String(val), m + 30, y); y += 5; if (y > 275) { pdf.addPage(); y = 15; } });
    y = Math.max(y + 8, 268); pdf.setDrawColor(36, 84, 156); pdf.line(m, y, pw - m, y); y += 4;
    pdf.setFontSize(6); pdf.setTextColor(130); pdf.text('This is a system-generated Order Confirmation from Prismoline ERP. This is not a tax invoice.', pw / 2, y, { align: 'center' });
    if (includeBOM && isSignage && oc.bom_items?.length > 0) {
      pdf.addPage(); y = 15; pdf.setFillColor(36, 84, 156); pdf.rect(0, 0, pw, 22, 'F');
      pdf.setTextColor(255); pdf.setFontSize(12); pdf.setFont(undefined, 'bold'); pdf.text('BILL OF MATERIALS (BOM)', m, 14);
      pdf.setFontSize(7); pdf.setFont(undefined, 'normal'); pdf.text(`OC: ${oc.oc_number} | Project: ${oc.project_name || '-'}`, m, 19); y = 30;
      pdf.setFillColor(36, 84, 156); pdf.rect(m, y, pw - 2 * m, 7, 'F'); pdf.setTextColor(255); pdf.setFontSize(7); pdf.setFont(undefined, 'bold');
      ['#', 'Material', 'Description', 'HSN', 'Qty', 'Unit'].forEach((h, i) => { pdf.text(h, [m + 2, m + 12, m + 50, m + 110, m + 135, m + 155][i], y + 5); }); y += 9;
      pdf.setTextColor(0); pdf.setFont(undefined, 'normal');
      (oc.bom_items || []).forEach((item, idx) => { pdf.text(`${idx + 1}`, m + 2, y); pdf.text(item.material || '', m + 12, y); const desc = item.description?.length > 40 ? item.description.substring(0, 40) + '...' : item.description || ''; pdf.text(desc, m + 50, y); pdf.text(item.hsn_code || '', m + 110, y); pdf.text(`${item.qty || ''}`, m + 135, y); pdf.text(item.unit || '', m + 155, y); y += 6; if (y > 270) { pdf.addPage(); y = 15; } });
      y += 5; pdf.setFontSize(6); pdf.setTextColor(130); pdf.text('BOM for consumption entry in Tally. Rates to be filled as per purchase costs.', m, y);
    }
    return pdf;
  };

  const handleDownload = (oc) => { generateOCPdf(oc, true).save(`${oc.oc_number}.pdf`); };
  const handlePrint = (oc) => { const blob = generateOCPdf(oc, true).output('blob'); const url = URL.createObjectURL(blob); const w = window.open(url); if (w) w.onload = () => { w.print(); }; };
  const handleSearchChange = (val) => { setSearch(val); setPage(1); };
  const handleStatusChange = (val) => { setStatusFilter(val); setPage(1); };

  const handleBulkUpdate = async (status) => {
    if (!window.confirm(`Update ${selectedOCs.size} OCs to "${status}"?`)) return;
    try {
      setBulkLoading(true);
      const result = await orderConfirmationAPI.bulkUpdateStatus([...selectedOCs], status);
      toast.success(`${result.updated} OCs updated to ${status}`);
      setSelectedOCs(new Set()); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk update failed'); }
    finally { setBulkLoading(false); }
  };

  // ===== DETAIL VIEW =====
  if (view === 'detail' && selectedOC) {
    return (
      <OCDetailView
        oc={selectedOC} amendments={amendments} confirmLoading={confirmLoading}
        canMarkDispatch={canMarkDispatch} canAddInvoice={canAddInvoice} canAmend={canAmend}
        canConfirmDispatch={canConfirmDispatch} confirmDispatchLoading={confirmDispatchLoading}
        branches={branches}
        onBack={() => { setView('list'); setSelectedOC(null); setAmendments([]); }}
        onEditDraft={handleEditDraft} onConfirmDraft={handleConfirmDraft}
        onAmend={openAmendDialog} onDispatch={openDispatchDialog} onInvoice={openInvoiceDialog}
        onConfirmDispatch={handleConfirmDispatch}
        onDownload={handleDownload} onPrint={handlePrint}
      />
    );
  }

  // ===== CREATE VIEW (Direct) =====
  if (view === 'create' && sourceMode === 'direct') {
    return (
      <>
      <div data-testid="oc-create-direct">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => { setView('list'); setSourceMode(null); setEditDraftMode(false); }} className="p-1.5 rounded-lg hover:bg-gray-100"><ArrowLeft className="w-4 h-4 text-gray-500" /></button>
          <div>
            <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>
              {editDraftMode ? 'Edit Draft OC' : 'Create Order Confirmation'}
            </h2>
            <p className="text-[11px] text-gray-500">{editDraftMode ? 'Update draft before confirming' : 'Direct OC without quotation reference'}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-3 space-y-4">
            {/* Customer Selection */}
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Customer</h3>
              <CustomerSearchInput
                value={form.customer.name || ''}
                customerId={form.customer.id || ''}
                onSelect={(c) => selectCustomer(c)}
                onClear={clearCustomer}
                placeholder="Search customer by name, GSTIN, or email..."
                testId="oc-customer-select"
              />
              {form.customer.id && (
                <div className="mt-2 p-2 bg-gray-50 rounded-lg text-[10px] text-gray-600 space-y-0.5">
                  {form.customer.address && <p>{form.customer.address}</p>}
                  {form.customer.city && <p>{form.customer.city}{form.customer.state ? `, ${form.customer.state}` : ''}{form.customer.pincode ? ` - ${form.customer.pincode}` : ''}</p>}
                  {form.customer.gstin && <p>GSTIN: <span className="font-mono font-semibold">{form.customer.gstin}</span></p>}
                  {form.customer.phone && <p>Phone: {form.customer.phone}</p>}
                  {form.customer.email && <p>Email: {form.customer.email}</p>}
                </div>
              )}
              {creditCheck && (
                <div className={`mt-2 p-2 rounded-lg text-[10px] ${creditCheck.exceeded ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                  Credit Limit: INR {creditCheck.credit_limit?.toLocaleString()} | Outstanding: INR {creditCheck.outstanding?.toLocaleString()} | Available: INR {creditCheck.available?.toLocaleString()}
                </div>
              )}
            </div>

            {/* Billing / Ship From */}
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Ship From (Billing Address)</h3>
              <select value={form.billing_address?.id || ''} onChange={e => {
                const ba = billingAddresses.find(b => b.id === e.target.value);
                setForm(prev => ({ ...prev, billing_address: ba || null }));
              }} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white">
                <option value="">Select billing address...</option>
                {billingAddresses.map(b => <option key={b.id} value={b.id}>{b.name} — {b.city}, {b.state}</option>)}
              </select>
            </div>

            {/* Ship To */}
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Ship To (Delivery Address)</h3>
                <label className="flex items-center gap-1.5 text-[10px] text-gray-500 cursor-pointer">
                  <input type="checkbox" checked={form.ship_to_same} onChange={e => setForm(prev => ({ ...prev, ship_to_same: e.target.checked, ship_to: e.target.checked ? null : prev.ship_to }))} className="rounded border-gray-300" />
                  Same as customer
                </label>
              </div>
              {!form.ship_to_same && (
                <div className="space-y-2">
                  {(customerShipToAddresses.length > 0 || savedShipTo.length > 0) && (
                    <select value="" onChange={e => { const val = e.target.value; const [type, idx] = val.split('::'); let sa; if (type === 'cust') { sa = customerShipToAddresses[parseInt(idx)]; } else { sa = savedShipTo.find(s => s.id === idx); } if (sa) setForm(prev => ({ ...prev, ship_to: { name: sa.name || sa.label || '', address: sa.address || '', city: sa.city || '', state: sa.state || '', pincode: sa.pin || sa.pincode || '', contact_person: sa.contact_person || '', phone: sa.contact_phone || sa.phone || '', gstin: sa.gstin || '' } })); }}
                      className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white mb-2">
                      <option value="">Load saved address...</option>
                      {customerShipToAddresses.length > 0 && <option disabled>— Customer Addresses —</option>}
                      {customerShipToAddresses.map((s, i) => <option key={`cust-${i}`} value={`cust::${i}`}>{s.name || s.label || ''} — {s.city || s.address || ''}</option>)}
                      {savedShipTo.length > 0 && <option disabled>— Saved Addresses —</option>}
                      {savedShipTo.map(s => <option key={s.id} value={`saved::${s.id}`}>{s.label ? `${s.label} — ` : ''}{s.name} — {s.city}</option>)}
                    </select>
                  )}
                  <Input placeholder="Company/Name" value={form.ship_to?.name || ''} onChange={e => setForm(prev => ({ ...prev, ship_to: { ...prev.ship_to, name: e.target.value } }))} className="text-xs" />
                  <Input placeholder="Address" value={form.ship_to?.address || ''} onChange={e => setForm(prev => ({ ...prev, ship_to: { ...prev.ship_to, address: e.target.value } }))} className="text-xs" />
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="City" value={form.ship_to?.city || ''} onChange={e => setForm(prev => ({ ...prev, ship_to: { ...prev.ship_to, city: e.target.value } }))} className="text-xs" />
                    <Input placeholder="State" value={form.ship_to?.state || ''} onChange={e => setForm(prev => ({ ...prev, ship_to: { ...prev.ship_to, state: e.target.value } }))} className="text-xs" />
                    <Input placeholder="Pincode" value={form.ship_to?.pincode || ''} onChange={e => setForm(prev => ({ ...prev, ship_to: { ...prev.ship_to, pincode: e.target.value } }))} className="text-xs" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Contact Person" value={form.ship_to?.contact_person || ''} onChange={e => setForm(prev => ({ ...prev, ship_to: { ...prev.ship_to, contact_person: e.target.value } }))} className="text-xs" />
                    <Input placeholder="Phone" value={form.ship_to?.phone || ''} onChange={e => setForm(prev => ({ ...prev, ship_to: { ...prev.ship_to, phone: e.target.value } }))} className="text-xs" />
                  </div>
                  <Input placeholder="GSTIN" value={form.ship_to?.gstin || ''} onChange={e => setForm(prev => ({ ...prev, ship_to: { ...prev.ship_to, gstin: e.target.value } }))} className="text-xs" />
                  {form.ship_to?.name && form.ship_to?.address && (
                    <button type="button" data-testid="save-ship-to-btn" onClick={async () => {
                      try {
                        await savedAddressAPI.create({ address_type: 'ship_to', label: '', name: form.ship_to.name, address: form.ship_to.address, city: form.ship_to.city || '', state: form.ship_to.state || '', pin: form.ship_to.pincode || '', contact_person: form.ship_to.contact_person || '', contact_phone: form.ship_to.phone || '', gstin: form.ship_to.gstin || '', notes: '' });
                        toast.success('Ship To address saved to Address Book');
                        const data = await savedAddressAPI.getByType('ship_to'); setSavedShipTo(data);
                      } catch { toast.error('Failed to save address'); }
                    }} className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 rounded-md hover:bg-emerald-100 transition-colors w-fit">
                      <BookmarkPlus className="w-3 h-3" /> Save to Address Book
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Consignee */}
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Consignee</h3>
                <label className="flex items-center gap-1.5 text-[10px] text-gray-500 cursor-pointer">
                  <input type="checkbox" checked={form.consignee_same} onChange={e => setForm(prev => ({ ...prev, consignee_same: e.target.checked, consignee: e.target.checked ? null : prev.consignee }))} className="rounded border-gray-300" />
                  Same as customer
                </label>
              </div>
              {!form.consignee_same && (
                <div className="space-y-2">
                  {savedConsignee.length > 0 && (
                    <select value="" onChange={e => { const sa = savedConsignee.find(s => s.id === e.target.value); if (sa) setForm(prev => ({ ...prev, consignee: { name: sa.name, address: sa.address, city: sa.city, state: sa.state, pincode: sa.pin || sa.pincode || '', contact_person: sa.contact_person, phone: sa.contact_phone || sa.phone || '', gstin: sa.gstin } })); }}
                      className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white mb-2">
                      <option value="">Load saved address...</option>
                      {savedConsignee.map(s => <option key={s.id} value={s.id}>{s.label ? `${s.label} — ` : ''}{s.name} — {s.city}</option>)}
                    </select>
                  )}
                  <Input placeholder="Company/Name" value={form.consignee?.name || ''} onChange={e => setForm(prev => ({ ...prev, consignee: { ...prev.consignee, name: e.target.value } }))} className="text-xs" />
                  <Input placeholder="Address" value={form.consignee?.address || ''} onChange={e => setForm(prev => ({ ...prev, consignee: { ...prev.consignee, address: e.target.value } }))} className="text-xs" />
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="City" value={form.consignee?.city || ''} onChange={e => setForm(prev => ({ ...prev, consignee: { ...prev.consignee, city: e.target.value } }))} className="text-xs" />
                    <Input placeholder="State" value={form.consignee?.state || ''} onChange={e => setForm(prev => ({ ...prev, consignee: { ...prev.consignee, state: e.target.value } }))} className="text-xs" />
                    <Input placeholder="Pincode" value={form.consignee?.pincode || ''} onChange={e => setForm(prev => ({ ...prev, consignee: { ...prev.consignee, pincode: e.target.value } }))} className="text-xs" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Contact Person" value={form.consignee?.contact_person || ''} onChange={e => setForm(prev => ({ ...prev, consignee: { ...prev.consignee, contact_person: e.target.value } }))} className="text-xs" />
                    <Input placeholder="Phone" value={form.consignee?.phone || ''} onChange={e => setForm(prev => ({ ...prev, consignee: { ...prev.consignee, phone: e.target.value } }))} className="text-xs" />
                  </div>
                  <Input placeholder="GSTIN" value={form.consignee?.gstin || ''} onChange={e => setForm(prev => ({ ...prev, consignee: { ...prev.consignee, gstin: e.target.value } }))} className="text-xs" />
                  {form.consignee?.name && form.consignee?.address && (
                    <button type="button" data-testid="save-consignee-btn" onClick={async () => {
                      try {
                        await savedAddressAPI.create({ address_type: 'consignee', label: '', name: form.consignee.name, address: form.consignee.address, city: form.consignee.city || '', state: form.consignee.state || '', pin: form.consignee.pincode || '', contact_person: form.consignee.contact_person || '', contact_phone: form.consignee.phone || '', gstin: form.consignee.gstin || '', notes: '' });
                        toast.success('Consignee address saved to Address Book');
                        const data = await savedAddressAPI.getByType('consignee'); setSavedConsignee(data);
                      } catch { toast.error('Failed to save address'); }
                    }} className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 rounded-md hover:bg-emerald-100 transition-colors w-fit">
                      <BookmarkPlus className="w-3 h-3" /> Save to Address Book
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Items */}
            {isSignageForm ? (
              <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="oc-signage-edit-panel">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Signage Boards</h3>
                  <span className="text-[10px] text-gray-500 italic">Shape / dimensions / materials are locked — they drive auto-created BOMs &amp; Production Orders. Cancel and re-convert the quotation to change them.</span>
                </div>
                <div className="space-y-3">
                  {form.signage_items.map((si, idx) => {
                    const baseRate = Number(si.final_per_board) || 0;
                    const effectiveRate = Number(si.custom_per_pcs_rate) || baseRate;
                    return (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-100" data-testid={`oc-signage-row-${idx}`}>
                        {/* Locked structural info */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-2 text-[10px]">
                          <div><span className="text-gray-500">Shape:</span> <span className="font-semibold text-gray-800">{si.shape || '—'}</span></div>
                          <div><span className="text-gray-500">Dimensions:</span> <span className="font-semibold text-gray-800">{si.dimensions || `${si.length_mm || 0} × ${si.height_mm || 0} mm`}</span></div>
                          <div><span className="text-gray-500">Area:</span> <span className="font-semibold text-gray-800">{(si.area_sqft || 0).toFixed(2)} sqft</span></div>
                          <div><span className="text-gray-500">Base Rate/Board:</span> <span className="font-semibold text-gray-800">₹{baseRate.toLocaleString()}</span></div>
                          {si.base_material && <div><span className="text-gray-500">Base:</span> <span className="font-semibold text-gray-800">{si.base_material}</span></div>}
                          {si.retro_sheet && <div><span className="text-gray-500">Retro:</span> <span className="font-semibold text-gray-800">{si.retro_sheet}</span></div>}
                          {si.post_type && <div><span className="text-gray-500">Post:</span> <span className="font-semibold text-gray-800">{si.post_type} × {si.num_posts || 0}</span></div>}
                          {si.frame_type && <div><span className="text-gray-500">Frame:</span> <span className="font-semibold text-gray-800">{si.frame_type}</span></div>}
                        </div>

                        {/* Editable row — qty, rate, amount */}
                        <div className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-2">
                            <label className="text-[10px] text-gray-500 mb-0.5 block">Qty</label>
                            <Input type="number" min={1} value={si.qty || 0}
                              onChange={e => updateSignageItem(idx, 'qty', Number(e.target.value))}
                              data-testid={`oc-signage-qty-${idx}`}
                              className="text-xs h-9" />
                          </div>
                          <div className="col-span-3">
                            <label className="text-[10px] text-gray-500 mb-0.5 block">Rate/Board (₹)</label>
                            <Input type="number" min={0} value={effectiveRate}
                              onChange={e => updateSignageItem(idx, 'custom_per_pcs_rate', Number(e.target.value))}
                              data-testid={`oc-signage-rate-${idx}`}
                              className="text-xs h-9" />
                            {si.pricing_mode === 'per_pcs' && Number(si.custom_per_pcs_rate) !== baseRate && (
                              <p className="text-[9px] text-amber-600 mt-0.5">Overridden from ₹{baseRate.toLocaleString()}</p>
                            )}
                          </div>
                          <div className="col-span-7 text-right">
                            <label className="text-[10px] text-gray-500 mb-0.5 block">Line Total</label>
                            <div className="text-sm font-bold text-gray-900" data-testid={`oc-signage-total-${idx}`}>
                              ₹{(si.total_amount || 0).toLocaleString()}
                            </div>
                          </div>
                        </div>

                        {/* Editable descriptions */}
                        <div className="mt-2 space-y-1.5">
                          <div>
                            <label className="text-[10px] text-gray-500 mb-0.5 block">Item Description</label>
                            <Input value={si.description || ''}
                              onChange={e => updateSignageItem(idx, 'description', e.target.value)}
                              data-testid={`oc-signage-desc-${idx}`}
                              placeholder="Short description shown on OC / Invoice"
                              className="text-xs h-9" />
                          </div>
                          <div>
                            <label className="text-[10px] text-gray-500 mb-0.5 block">Specific Description / Notes</label>
                            <textarea value={si.specific_description || ''}
                              onChange={e => updateSignageItem(idx, 'specific_description', e.target.value)}
                              data-testid={`oc-signage-specdesc-${idx}`}
                              placeholder="Extra site-specific notes / installation notes"
                              rows={2}
                              className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-200" />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Items</h3>
                <button data-testid="oc-add-item" onClick={() => setForm(prev => ({ ...prev, items: [...prev.items, { ...emptyItem }] }))}
                  className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100">
                  <Plus className="w-3 h-3" /> Add Item
                </button>
              </div>
              {/* Column Headers + Items — scrollable */}
              <div className="overflow-x-auto pb-2">
                <div style={{ minWidth: '920px' }}>
                  <div className="grid gap-2 px-2 mb-1.5" style={{ gridTemplateColumns: '130px 220px 80px 130px 70px 110px 70px 110px' }}>
                    <span className="text-[10px] font-bold text-gray-500 uppercase">Item ID</span>
                    <span className="text-[10px] font-bold text-gray-500 uppercase">Product Name</span>
                    <span className="text-[10px] font-bold text-gray-500 uppercase">HSN</span>
                    <span className="text-[10px] font-bold text-gray-500 uppercase">Quantity{form.branch ? ' / Stock' : ''}</span>
                    <span className="text-[10px] font-bold text-gray-500 uppercase">Unit</span>
                    <span className="text-[10px] font-bold text-gray-500 uppercase">Unit Price</span>
                    <span className="text-[10px] font-bold text-gray-500 uppercase">Disc%</span>
                    <span className="text-[10px] font-bold text-gray-500 uppercase text-right">Amount</span>
                  </div>
                  <div className="space-y-2">
                    {form.items.map((item, idx) => (
                      <div key={idx} className="flex gap-2 items-start p-2 bg-gray-50 rounded-lg relative" data-testid={`oc-item-row-${idx}`}>
                        <button
                          onClick={() => setForm(prev => {
                            if (prev.items.length <= 1) {
                              return { ...prev, items: [{ product_id: '', _product_id: '', product_name: '', sku: '', description: '', hsn_code: '', qty: 0, unit: '', rate: 0, discount: 0, amount: 0, _allowedUOMs: [] }] };
                            }
                            return { ...prev, items: prev.items.filter((_, i) => i !== idx) };
                          })}
                          data-testid={`oc-item-remove-${idx}`}
                          title="Remove this row"
                          aria-label="Remove line item"
                          className="absolute -top-2 -right-2 z-10 flex items-center justify-center w-7 h-7 rounded-full bg-white border border-red-300 text-red-500 hover:bg-red-500 hover:text-white hover:border-red-500 shadow-sm transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <div className="flex-1 space-y-1.5">
                          <div className="grid gap-2" style={{ gridTemplateColumns: '130px 220px 80px 130px 70px 110px 70px 110px' }}>
                        <div>
                          <Input value={item.sku || ''} readOnly className="text-xs bg-gray-100 text-gray-600 font-mono h-9" placeholder="—" data-testid={`oc-sku-${idx}`} title="SKU auto-filled from product" />
                        </div>
                        <div className="relative">
                          <ProductSearchInput
                            value={item.product_name || ''}
                            productId={item._product_id || ''}
                            branchId={form.branch || ''}
                            onSelect={(p) => handleOCProductSelect(idx, p)}
                            onChange={(text) => updateItem(idx, 'product_name', text)}
                            onClear={() => handleOCProductClear(idx)}
                            placeholder="Search product..."
                            testId={`oc-product-${idx}`}
                          />
                        </div>
                        <Input value={item.hsn_code || ''} onChange={e => updateItem(idx, 'hsn_code', e.target.value)} placeholder="HSN" className="text-xs h-9" />
                        <div className="flex flex-col gap-1 min-w-0">
                          <Input type="number" value={item.qty} onChange={e => updateItem(idx, 'qty', Number(e.target.value))} className="w-full text-xs text-right h-9 min-w-[60px]" data-testid={`oc-qty-${idx}`} min={1} />
                          {form.branch && item._product_id && (() => {
                            const detail = branchStockDetails[item._product_id];
                            const physical = detail?.physical_balance ?? branchStock[item._product_id] ?? 0;
                            const reserved = detail?.reserved_awaiting_invoice ?? 0;
                            const available = detail?.available ?? branchStock[item._product_id] ?? 0;
                            const isLoading = branchStock[item._product_id] === undefined;
                            const sufficient = available >= item.qty;
                            const tip = reserved > 0
                              ? `Physical: ${physical} | Reserved (Awaiting Invoice): ${reserved} | Available: ${available}`
                              : `Available: ${available}`;
                            return (
                              <span
                                className={`block text-[9px] font-semibold leading-tight px-1 py-0.5 rounded text-center truncate ${sufficient ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}
                                data-testid={`oc-stock-${idx}`}
                                title={tip}
                              >
                                {isLoading ? '...' : available} stk
                                {reserved > 0 && (
                                  <span className="ml-1 text-amber-700" data-testid={`oc-stock-reserved-${idx}`}>(−{reserved} res)</span>
                                )}
                              </span>
                            );
                          })()}
                        </div>
                        {(() => {
                          const allowed = item._allowedUOMs || [];
                          const allUOMs = ['PCS', 'KG', 'MTR', 'SQFT', 'SET', 'LOT', 'NOS', 'RFT', 'PAIR', 'DRUM', 'LTR', 'ROLL', 'PKT', 'MT', 'SQMT'];
                          const hasProduct = !!item._product_id;
                          const options = hasProduct && allowed.length > 0 ? allowed.map(u => u.toUpperCase()) : allUOMs;
                          if (item.unit && !options.includes(item.unit)) options.unshift(item.unit);
                          return (
                            <select value={item.unit} onChange={e => updateItem(idx, 'unit', e.target.value)}
                              className="px-1 py-1 text-xs border border-gray-200 rounded h-9 bg-white"
                              title={hasProduct && allowed.length > 0 ? `Available UOMs: ${allowed.join(', ')}` : 'Select unit'}>
                              {options.filter((v, i, a) => a.indexOf(v) === i).map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                          );
                        })()}
                        <Input type="number" value={item.rate} onChange={e => updateItem(idx, 'rate', Number(e.target.value))} className="text-xs text-right h-9" data-testid={`oc-rate-${idx}`} />
                        <Input type="number" value={item.discount} onChange={e => updateItem(idx, 'discount', Number(e.target.value))} placeholder="0" className="text-xs text-right h-9" min={0} max={100} />
                        <div className="flex items-center justify-end">
                          <span className="text-sm font-bold text-gray-800" data-testid={`oc-amount-${idx}`}>{(item.amount || 0).toLocaleString()}</span>
                        </div>
                      </div>
                      <input
                        value={item.description || ''}
                        onChange={e => updateItem(idx, 'description', e.target.value)}
                        placeholder="Item description / specifications..."
                        data-testid={`oc-desc-${idx}`}
                        className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200 bg-white"
                      />
                    </div>
                    {/* delete button moved to top-right floating position */}
                  </div>
                ))}
              </div>
                </div>
              </div>
            </div>
            )}

            {/* Stock check result */}
            {stockCheck && (
              <div className={`p-3 rounded-lg text-xs ${stockCheck.can_proceed ? (stockCheck.all_sufficient ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-amber-50 border border-amber-200 text-amber-700') : 'bg-red-50 border border-red-200 text-red-700'}`} data-testid="oc-stock-check-banner">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    {stockCheck.message}
                    {stockCheck.items && !stockCheck.all_sufficient && (
                      <ul className="mt-1 space-y-0.5 text-[10px]">
                        {stockCheck.items.filter(i => !i.sufficient).map((i, idx) => (
                          <li key={idx}>{i.product_name}: need {i.required_qty}, have {i.available_qty} (short {i.shortfall})</li>
                        ))}
                      </ul>
                    )}
                    <div className="text-[9px] opacity-70 mt-1">Last checked: {stockCheck._checkedAt || 'just now'}</div>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!form.branch) return;
                      setStockCheckLoading(true);
                      try {
                        const r = await orderConfirmationAPI.stockCheck({ branch: form.branch, items: form.items });
                        setStockCheck({ ...r, _checkedAt: new Date().toLocaleTimeString() });
                      } catch (e) { console.error(e); }
                      finally { setStockCheckLoading(false); }
                    }}
                    disabled={stockCheckLoading}
                    data-testid="oc-stock-check-refresh"
                    className="text-[10px] underline whitespace-nowrap hover:opacity-80 disabled:opacity-50"
                  >{stockCheckLoading ? 'Checking…' : 'Refresh'}</button>
                </div>
              </div>
            )}
          </div>

          {/* Right sidebar */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Details</h3>
              <div className="space-y-2">
                <div><label className="text-[10px] text-gray-500 mb-0.5 block">Type</label>
                  <select value={form.oc_type} disabled={isSignageForm} onChange={e => {
                    const val = e.target.value;
                    setForm(prev => ({
                      ...prev, oc_type: val,
                      ...(val === 'retail' ? { project_name: '', site_project_id: '', site_project_code: '' } : {}),
                    }));
                  }} data-testid="oc-type-select"
                    className={`w-full px-2 py-1.5 text-xs border border-gray-200 rounded ${isSignageForm ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : 'bg-white'}`}
                    title={isSignageForm ? 'Signage OCs drive auto-created Products / BOMs / Production Orders — type is locked' : ''}>
                    <option value="project">Project</option>
                    <option value="retail">Retail</option>
                    {isSignageForm && <option value="signage">Signage</option>}
                    {/* Legacy generic type (from pre-Signage era OCs) — shown only when already set */}
                    {form.oc_type === 'generic' && <option value="generic">Generic</option>}
                  </select>
                  {isSignageForm && (
                    <p className="text-[10px] text-amber-700 mt-0.5">Type locked — signage structure cannot be changed post-conversion.</p>
                  )}
                </div>
                <div><label className="text-[10px] text-gray-500 mb-0.5 block">Site Project</label>
                  <select data-testid="oc-site-project-select" value={form.site_project_id} disabled={form.oc_type === 'retail'} onChange={e => {
                    const proj = siteProjects.find(p => p.id === e.target.value);
                    setForm(prev => ({
                      ...prev,
                      site_project_id: proj?.id || '',
                      site_project_code: proj?.project_code || '',
                      project_name: proj ? `${proj.project_code} - ${proj.project_name}` : prev.project_name,
                    }));
                  }} className={`w-full px-2 py-1.5 text-xs border border-gray-200 rounded ${form.oc_type === 'retail' ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white'}`}>
                    <option value="">-- No project linked --</option>
                    {siteProjects.map(p => (
                      <option key={p.id} value={p.id}>{p.project_code} — {p.project_name}{p.customer_name ? ` (${p.customer_name})` : ''}</option>
                    ))}
                  </select>
                  {form.site_project_code && <p className="text-[10px] text-blue-600 font-mono mt-0.5">{form.site_project_code}</p>}
                </div>
                <div><label className="text-[10px] text-gray-500 mb-0.5 block">Project Name {form.site_project_id ? '(auto-filled)' : ''}</label>
                  <Input value={form.project_name} onChange={e => setForm(prev => ({ ...prev, project_name: e.target.value }))} disabled={form.oc_type === 'retail'} className={`text-xs ${form.oc_type === 'retail' ? 'bg-gray-100 text-gray-400' : ''}`} placeholder={form.site_project_id ? '' : 'Free-text project name (optional)'} /></div>
                <div><label className="text-[10px] text-gray-500 mb-0.5 block">Branch</label>
                  <select data-testid="oc-branch-select" value={form.branch} onChange={e => setForm(prev => ({ ...prev, branch: e.target.value }))} className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-white">
                    <option value="">Select Branch...</option>
                    {filterBranchesForUser(branches).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select></div>
                <div><label className="text-[10px] text-gray-500 mb-0.5 block">Salesman *</label>
                  <select data-testid="oc-salesman-select" value={form.salesman_name} onChange={e => {
                    const sp = salespersons.find(s => s.name === e.target.value);
                    setForm(prev => ({ ...prev, salesman_name: e.target.value, salesman_phone: sp?.phone || '' }));
                  }} className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-white">
                    <option value="">Select Salesman...</option>
                    {salespersons.map(sp => <option key={sp.id} value={sp.name}>{sp.name}</option>)}
                  </select></div>
                <div><label className="text-[10px] text-gray-500 mb-0.5 block">GST Rate (%)</label>
                  <select value={form.gst_rate} onChange={e => setForm(prev => ({ ...prev, gst_rate: Number(e.target.value) }))} className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-white">
                    {[0, 5, 12, 18, 28].map(r => <option key={r} value={r}>{r}%</option>)}
                  </select></div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Extra Charges</h3>
                <button onClick={() => setForm(prev => ({ ...prev, other_charges: [...(prev.other_charges || []), { description: '', amount: 0, taxable: true }] }))}
                  className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold text-blue-700 bg-blue-50 rounded hover:bg-blue-100" data-testid="oc-add-extra-charge">
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
              {(form.other_charges || []).length === 0 ? (
                <p className="text-[10px] text-gray-400 italic">No extra charges added</p>
              ) : (
                <div className="space-y-1.5">
                  {form.other_charges.map((ch, ci) => (
                    <div key={ci} className="flex gap-2 items-center" data-testid={`oc-extra-charge-${ci}`}>
                      <Input value={ch.description} onChange={e => {
                        const charges = [...form.other_charges]; charges[ci] = { ...charges[ci], description: e.target.value };
                        setForm(prev => ({ ...prev, other_charges: charges }));
                      }} placeholder="Description" className="flex-1 text-xs h-8" />
                      <Input type="number" value={ch.amount} onChange={e => {
                        const charges = [...form.other_charges]; charges[ci] = { ...charges[ci], amount: Number(e.target.value) };
                        setForm(prev => ({ ...prev, other_charges: charges }));
                      }} placeholder="Amount" className="w-24 text-xs text-right h-8" />
                      <label className="flex items-center gap-1 text-[10px] text-gray-500 whitespace-nowrap">
                        <input type="checkbox" checked={ch.taxable !== false} onChange={e => {
                          const charges = [...form.other_charges]; charges[ci] = { ...charges[ci], taxable: e.target.checked };
                          setForm(prev => ({ ...prev, other_charges: charges }));
                        }} className="w-3 h-3" /> GST
                      </label>
                      <button onClick={() => {
                        const charges = form.other_charges.filter((_, i) => i !== ci);
                        setForm(prev => ({ ...prev, other_charges: charges }));
                      }} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Totals</h3>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between"><span className="text-gray-500">Subtotal (Items)</span><span className="font-semibold">INR {subtotal.toLocaleString()}</span></div>
                {chargesTotal > 0 && <div className="flex justify-between"><span className="text-gray-500">Extra Charges</span><span className="font-semibold">INR {chargesTotal.toLocaleString()}</span></div>}
                {isIntraState ? (<><div className="flex justify-between text-gray-500"><span>CGST ({form.gst_rate / 2}%)</span><span>{cgst.toLocaleString()}</span></div><div className="flex justify-between text-gray-500"><span>SGST ({form.gst_rate / 2}%)</span><span>{sgst.toLocaleString()}</span></div></>) : (<div className="flex justify-between text-gray-500"><span>IGST ({form.gst_rate}%)</span><span>{igst.toLocaleString()}</span></div>)}
                <div className="flex justify-between text-sm font-bold border-t border-gray-200 pt-1"><span>Total</span><span style={{ color: '#24549c' }}>INR {total.toLocaleString()}</span></div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Terms</h3>
              <div className="space-y-2">
                {['payment_terms', 'delivery_schedule', 'warranty'].map(f => (
                  <div key={f}><label className="text-[10px] text-gray-500 mb-0.5 block capitalize">{f.replace(/_/g, ' ')}</label>
                    <Input value={form.terms[f] || ''} onChange={e => setForm(prev => ({ ...prev, terms: { ...prev.terms, [f]: e.target.value } }))} className="text-xs" /></div>
                ))}
                <div><label className="text-[10px] text-gray-500 mb-0.5 block">Freight</label>
                  <select value={form.terms.freight || ''} onChange={e => setForm(prev => ({ ...prev, terms: { ...prev.terms, freight: e.target.value } }))} className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-white">
                    {freightOptions.map(f => <option key={f}>{f}</option>)}
                  </select></div>
                <div><label className="text-[10px] text-gray-500 mb-0.5 block">Additional Notes</label>
                  <Textarea rows={2} value={form.terms.notes || ''} onChange={e => setForm(prev => ({ ...prev, terms: { ...prev.terms, notes: e.target.value } }))} className="text-xs" /></div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <label className="text-[10px] text-gray-500 mb-0.5 block">Remarks</label>
              <Textarea rows={2} value={form.remarks} onChange={e => setForm(prev => ({ ...prev, remarks: e.target.value }))} className="text-xs" />

              <button data-testid="oc-create-submit" onClick={editDraftMode ? handleSaveDraftEdit : handleCreateDirect} disabled={submitting || (stockCheck && !stockCheck.can_proceed)}
                className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
                {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {editDraftMode ? 'Save Draft Changes' : 'Create Order Confirmation'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Shortfall Production Modal */}
      {showShortfallModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="shortfall-modal-overlay">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 overflow-hidden" data-testid="shortfall-modal">
            <div className="px-6 py-4 border-b border-gray-100 bg-amber-50">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Stock Shortfall Detected</h3>
                  <p className="text-[11px] text-gray-500">{stockCheck?.branch_name || 'Production Branch'}</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4">
              <p className="text-xs text-gray-600 mb-4">The following items have insufficient stock. You can auto-create Production Orders for the shortfall quantities.</p>
              <div className="rounded-lg border border-gray-200 overflow-hidden mb-4">
                <table className="w-full text-[11px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-gray-500">Product</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-500">Need</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-500">Avail</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-500 w-28">Prod Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shortfallItems.map((item, idx) => (
                      <tr key={idx} className="border-t border-gray-100">
                        <td className="px-3 py-2">
                          <span className="font-semibold text-gray-900">{item.product_name}</span>
                          {item.product_id && <span className="block text-[10px] font-mono text-gray-400">{item.product_id}</span>}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700">{item.required_qty} {item.unit}</td>
                        <td className="px-3 py-2 text-right text-gray-700">{item.available_qty}</td>
                        <td className="px-3 py-2 text-right">
                          <input type="number" min="0" step="any" value={item.shortfall_qty}
                            data-testid={`shortfall-qty-create-${idx}`}
                            onChange={e => {
                              const val = parseFloat(e.target.value) || 0;
                              setShortfallItems(prev => prev.map((si, i) => i === idx ? { ...si, shortfall_qty: val } : si));
                            }}
                            className="w-full px-2 py-1.5 text-right text-[11px] font-semibold border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-300 bg-amber-50"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-gray-400">Production orders will be created in "Planned" status. You can edit materials from the Production module.</p>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-2">
              <button onClick={() => { setShowShortfallModal(false); setShortfallItems([]); }} disabled={shortfallCreating}
                data-testid="shortfall-cancel-btn-create"
                className="px-4 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                Cancel
              </button>
              <div className="flex items-center gap-2">
                <button onClick={handleShortfallCreateOCOnly} disabled={shortfallCreating}
                  data-testid="shortfall-oc-only-btn-create"
                  className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                  {shortfallCreating ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
                  Create OC Only
                </button>
                <button onClick={handleShortfallCreateWithProduction} disabled={shortfallCreating}
                  data-testid="shortfall-oc-prod-btn-create"
                  className="px-4 py-2 text-xs font-semibold text-white rounded-lg disabled:opacity-50 hover:shadow-lg"
                  style={{ backgroundColor: '#24549c' }}>
                  {shortfallCreating ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
                  Create OC + Production Orders
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </>
    );
  }

  // ===== CREATE VIEW (From Quotation) =====
  if (view === 'create' && sourceMode === 'from-quote') {
    const baseList = quoteSearchResults !== null ? quoteSearchResults : quotes;
    const acceptableQuotes = baseList.filter(q => q.status !== 'Rejected');
    return (
      <div data-testid="oc-from-quote">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => { setView('list'); setSourceMode(null); setQuoteSearch(''); setQuoteSearchResults(null); }} className="p-1.5 rounded-lg hover:bg-gray-100"><ArrowLeft className="w-4 h-4 text-gray-500" /></button>
          <div><h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Convert Quotation to OC</h2><p className="text-[11px] text-gray-500">Search and select a quotation to convert</p></div>
        </div>
        <div className="relative mb-3 max-w-md">
          <input
            data-testid="oc-quote-search"
            value={quoteSearch}
            onChange={e => handleQuoteSearch(e.target.value)}
            placeholder="Search by quotation number or customer (e.g. GQ-JH-2605-009)..."
            className="w-full pl-9 pr-9 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200"
          />
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" /></svg>
          {quoteSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-gray-400" />}
        </div>
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead><tr className="text-[10px] font-bold text-gray-500 uppercase" style={{ backgroundColor: '#f8f9fb' }}>
              {['Quote #', 'Type', 'Customer', 'Total', 'Status', 'Action'].map(h => <th key={h} className="text-left px-4 py-3">{h}</th>)}
            </tr></thead>
            <tbody>
              {acceptableQuotes.map(q => (
                <tr key={q.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-3 text-xs font-mono font-semibold text-gray-700" data-testid={`quote-number-${q.id}`}>{q.quotation_number || q.id?.substring(0, 12)}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${q.quotation_type === 'signage' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>{q.quotation_type === 'signage' ? 'Signage' : 'Generic'}</span></td>
                  <td className="px-4 py-3 text-xs font-medium text-gray-900">{q.customer_name}</td>
                  <td className="px-4 py-3 text-xs font-bold" style={{ color: '#24549c' }}>INR {(q.total || 0).toLocaleString()}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${q.status === 'Accepted' ? 'bg-green-100 text-green-700' : q.status === 'Sent' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{q.status}</span></td>
                  <td className="px-4 py-3">
                    <button data-testid={`convert-quote-${q.id}`} onClick={() => handleCreateFromQuote(q.id)} disabled={submitting}
                      className="flex items-center gap-1 px-3 py-1 text-[10px] font-semibold text-white rounded-md disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
                      {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRightLeft className="w-3 h-3" />} Convert to OC
                    </button>
                  </td>
                </tr>
              ))}
              {acceptableQuotes.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-gray-400">{quoteSearch.trim() ? `No quotations found for "${quoteSearch.trim()}"` : 'No quotations available'}</td></tr>}
            </tbody>
          </table>
        </div>
        {quoteSearchResults === null && quotes.length >= 50 && !quoteSearch.trim() && (
          <p className="text-[11px] text-gray-400 mt-2">Showing the 50 most recent quotations. Use search above to find any older quotation.</p>
        )}
      </div>
    );
  }

  // ===== LIST VIEW =====
  return (
    <>
      <OCListView
        ocs={ocs} loading={loading} totalItems={totalItems} search={search} statusFilter={statusFilter}
        branchFilter={branchFilter} branches={branches}
        page={page} totalPages={totalPages} pageSize={pageSize}
        selectedOCs={selectedOCs} bulkLoading={bulkLoading}
        canMarkDispatch={canMarkDispatch} canAddInvoice={canAddInvoice} canAmend={canAmend}
        onSearchChange={handleSearchChange} onStatusChange={handleStatusChange}
        onBranchChange={(val) => { setBranchFilter(val); setPage(1); }}
        onPageChange={setPage} onPageSizeChange={handlePageSizeChange}
        onSelectOC={setSelectedOCs}
        onSelectAll={(checked) => checked ? setSelectedOCs(new Set(ocs.map(oc => oc.id))) : setSelectedOCs(new Set())}
        onToggleSelect={(id) => setSelectedOCs(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; })}
        onView={async (oc) => {
          // Open detail immediately for snappy UX, then refresh with the full
          // payload (which carries `stuck_awaiting_invoice_count` — only present
          // on the per-OC endpoint, not on the list).
          setSelectedOC(oc); setView('detail'); fetchAmendments(oc.id);
          try {
            const full = await orderConfirmationAPI.getById(oc.id);
            setSelectedOC(full);
          } catch { /* keep the list-level OC */ }
        }}
        onDownload={handleDownload} onPrint={handlePrint}
        onEditDraft={handleEditDraft} onConfirmDraft={handleConfirmDraft}
        onAmend={openAmendDialog} onDispatch={openDispatchDialog} onInvoice={openInvoiceDialog}
        onBulkUpdate={handleBulkUpdate}
        onCreateDirect={() => { setView('create'); setSourceMode('direct'); }}
        onFromQuote={() => { setView('create'); setSourceMode('from-quote'); }}
        sortConfig={sortConfig} onSort={handleSort}
        adminTools={isAdmin ? { onProductBindingAudit: openBindingDialog } : null}
      />

      <OCDispatchDialog
        open={showDispatchDialog} onOpenChange={setShowDispatchDialog} dispatchOC={dispatchOC}
        vehicleNumber={vehicleNumber} setVehicleNumber={setVehicleNumber}
        dispatchNotes={dispatchNotes} setDispatchNotes={setDispatchNotes}
        selectedReadyItems={selectedReadyItems} setSelectedReadyItems={setSelectedReadyItems}
        dispatchQtys={dispatchQtys} setDispatchQtys={setDispatchQtys}
        transporterName={transporterName} setTransporterName={setTransporterName}
        driverName={driverName} setDriverName={setDriverName}
        driverPhone={driverPhone} setDriverPhone={setDriverPhone}
        lrNumber={lrNumber} setLrNumber={setLrNumber}
        dispatchLoading={dispatchLoading} onConfirm={handleMarkReadyToDispatch}
      />

      <OCInvoiceDialog
        open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog} invoiceOC={invoiceOC}
        invoiceNumber={invoiceNumber} setInvoiceNumber={setInvoiceNumber}
        invoiceDate={invoiceDate} setInvoiceDate={setInvoiceDate}
        invoiceNotes={invoiceNotes} setInvoiceNotes={setInvoiceNotes}
        invoiceDocUrl={invoiceDocUrl} setInvoiceDocUrl={setInvoiceDocUrl}
        invoiceDocName={invoiceDocName} setInvoiceDocName={setInvoiceDocName}
        invoiceLoading={invoiceLoading} onConfirm={handleUpdateInvoice}
      />

      <OCAmendDialog
        open={showAmendDialog} onOpenChange={setShowAmendDialog} amendOC={amendOC}
        amendItems={amendItems} setAmendItems={setAmendItems}
        amendReason={amendReason} setAmendReason={setAmendReason}
        amendDetails={amendDetails} setAmendDetails={setAmendDetails}
        amendLoading={amendLoading} onSubmit={handleSubmitAmendment}
        customers={customers}
        branches={branches}
        billingAddresses={billingAddresses}
      />

      {/* Shortfall Production Modal */}
      {showShortfallModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="shortfall-modal-overlay">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 overflow-hidden" data-testid="shortfall-modal">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 bg-amber-50">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Stock Shortfall Detected</h3>
                  <p className="text-[11px] text-gray-500">{stockCheck?.branch_name || 'Production Branch'}</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-4">
              <p className="text-xs text-gray-600 mb-4">The following items have insufficient stock. You can auto-create Production Orders for the shortfall quantities.</p>

              <div className="rounded-lg border border-gray-200 overflow-hidden mb-4">
                <table className="w-full text-[11px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-gray-500">Product</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-500">Need</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-500">Avail</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-500 w-28">Prod Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shortfallItems.map((item, idx) => (
                      <tr key={idx} className="border-t border-gray-100">
                        <td className="px-3 py-2">
                          <span className="font-semibold text-gray-900">{item.product_name}</span>
                          {item.product_id && <span className="block text-[10px] font-mono text-gray-400">{item.product_id}</span>}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700">{item.required_qty} {item.unit}</td>
                        <td className="px-3 py-2 text-right text-gray-700">{item.available_qty}</td>
                        <td className="px-3 py-2 text-right">
                          <input type="number" min="0" step="any" value={item.shortfall_qty}
                            data-testid={`shortfall-qty-${idx}`}
                            onChange={e => {
                              const val = parseFloat(e.target.value) || 0;
                              setShortfallItems(prev => prev.map((si, i) => i === idx ? { ...si, shortfall_qty: val } : si));
                            }}
                            className="w-full px-2 py-1.5 text-right text-[11px] font-semibold border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-300 bg-amber-50"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-[10px] text-gray-400 mb-1">Production orders will be created in "Planned" status. You can edit materials from the Production module.</p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-2">
              <button onClick={() => { setShowShortfallModal(false); setShortfallItems([]); }} disabled={shortfallCreating}
                data-testid="shortfall-cancel-btn"
                className="px-4 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                Cancel
              </button>
              <div className="flex items-center gap-2">
                <button onClick={handleShortfallCreateOCOnly} disabled={shortfallCreating}
                  data-testid="shortfall-oc-only-btn"
                  className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                  {shortfallCreating ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
                  Create OC Only
                </button>
                <button onClick={handleShortfallCreateWithProduction} disabled={shortfallCreating}
                  data-testid="shortfall-oc-prod-btn"
                  className="px-4 py-2 text-xs font-semibold text-white rounded-lg disabled:opacity-50 hover:shadow-lg"
                  style={{ backgroundColor: '#24549c' }}>
                  {shortfallCreating ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
                  Create OC + Production Orders
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ ADMIN: Product-Binding Audit dialog ============ */}
      {showBindingDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="binding-audit-dialog">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] flex flex-col overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-gray-900">OC Product-Binding Audit</h3>
                <p className="text-[11px] text-gray-500 mt-0.5">Find &amp; heal OC items whose product_id no longer resolves to a current product master (root cause of &ldquo;I have stock but RTD says I don&rsquo;t&rdquo;).</p>
              </div>
              <button onClick={() => setShowBindingDialog(false)} className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {bindingLoading ? (
                <div className="flex items-center justify-center py-12 text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2" />Scanning…</div>
              ) : !bindingReport ? (
                <p className="text-xs text-gray-400 text-center py-6">No report loaded.</p>
              ) : (
                <>
                  {/* Summary */}
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-5" data-testid="binding-summary">
                    {[
                      { k: 'total_ocs_scanned', label: 'OCs scanned', tone: 'bg-gray-50 text-gray-700' },
                      { k: 'ocs_with_issues', label: 'OCs w/ issues', tone: 'bg-amber-50 text-amber-700' },
                      { k: 'items_fixable_via_sku', label: 'Fixable (SKU)', tone: 'bg-emerald-50 text-emerald-700' },
                      { k: 'items_fixable_via_name', label: 'Fixable (name)', tone: 'bg-emerald-50 text-emerald-700' },
                      { k: 'items_ambiguous', label: 'Ambiguous', tone: 'bg-orange-50 text-orange-700' },
                      { k: 'items_orphan', label: 'Orphan', tone: 'bg-red-50 text-red-700' },
                    ].map(c => (
                      <div key={c.k} className={`px-2 py-1.5 rounded-md ${c.tone}`}>
                        <p className="text-[9px] font-bold uppercase tracking-wider opacity-70">{c.label}</p>
                        <p className="text-sm font-bold">{bindingReport.summary?.[c.k] ?? 0}</p>
                      </div>
                    ))}
                  </div>

                  {/* Issues list */}
                  {(bindingReport.issues || []).length === 0 ? (
                    <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-700">
                      ✅ All active OC items have valid product bindings. Nothing to heal.
                    </div>
                  ) : (
                    <div className="space-y-3" data-testid="binding-issues-list">
                      {(bindingReport.issues || []).slice(0, 200).map(iss => (
                        <div key={iss.oc_id} className="border border-gray-100 rounded-lg p-3 bg-gray-50/50">
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="px-1.5 py-0.5 text-[10px] font-mono font-bold bg-blue-100 text-blue-700 rounded">{iss.oc_number}</span>
                            <span className="text-[10px] text-gray-500">{iss.status}</span>
                          </div>
                          <div className="space-y-1">
                            {iss.items.map(it => (
                              <div key={`${iss.oc_id}-${it.index}`} className="text-[11px] flex items-start gap-2">
                                <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded
                                  ${it.kind === 'fixable_via_sku' || it.kind === 'fixable_via_name' ? 'bg-emerald-100 text-emerald-700' :
                                   it.kind === 'ambiguous' ? 'bg-orange-100 text-orange-700' :
                                   'bg-red-100 text-red-700'}`}>{it.kind}</span>
                                <span className="text-gray-900">{it.product_name || <i className="text-gray-400">(no name)</i>}</span>
                                {it.proposed_target && (
                                  <span className="text-gray-500 ml-auto">→ {it.proposed_target.sku} ({it.proposed_target.name})</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      {bindingReport.issues.length > 200 && (
                        <p className="text-[10px] text-gray-400 text-center">…and {bindingReport.issues.length - 200} more (truncated). Run heal to fix all.</p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between gap-2">
              <p className="text-[10px] text-gray-500">
                Dry-run is safe; &ldquo;Apply Heal&rdquo; updates only items with a single unambiguous match. Ambiguous/orphan cases are skipped.
              </p>
              <div className="flex items-center gap-2">
                <button onClick={() => runHeal(false)} disabled={bindingLoading || healingActive}
                  className="px-3 py-1.5 text-[11px] font-semibold border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  data-testid="binding-dry-run-btn">
                  {healingActive ? 'Working…' : 'Dry-Run'}
                </button>
                <button onClick={() => runHeal(true)} disabled={bindingLoading || healingActive || !(bindingReport?.summary?.items_fixable_via_sku || bindingReport?.summary?.items_fixable_via_name)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg hover:shadow-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                  data-testid="binding-apply-heal-btn">
                  <Wrench className="w-3.5 h-3.5" /> {healingActive ? 'Healing…' : 'Apply Heal'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrderConfirmationModule;
